/**
 * What a publish refusal actually SAYS, per issue, in nine locales.
 *
 * THE DEFECT (owner, 2026-09-01): publish failed with
 *
 *     "This workflow is not ready to publish. Fix the highlighted setup, then try again."
 *
 * while the Readiness Checklist showed four green ticks and nothing on screen was highlighted.
 * Diagnosed by running `validate_process_flow` against the owner's actual draft in the local DB —
 * ONE issue, and the backend had named it exactly:
 *
 *     [condition_field_missing] Condition field must exist in this process form.
 *         node_id=flow-1788242454187  field='amount'
 *
 * A Condition step branched on a form field `amount` that the form does not contain. The backend
 * returns every issue with a `code`, an English `message` and its context; `translateApiError`
 * keys only off the top-level `code`, so all of that was discarded and replaced with a sentence
 * that pointed at a highlight nobody drew.
 *
 * WHY THIS FILE AND NOT `translateApiError`
 * -----------------------------------------
 * That helper answers "what is the ONE line for this error". A flow refusal is a LIST, each entry
 * naming a different step or field, and i18next cannot interpolate an array of objects into a
 * sentence. Different shape, different helper.
 *
 * WHY ONLY SOME CODES ARE TRANSLATED
 * ----------------------------------
 * `flow_validation.py` emits 31 issue codes. Roughly a third of them cannot be produced through
 * the wizard at all — `flow_not_object`, `nodes_not_array`, `node_not_object`, `duplicate_node_id`,
 * `edge_source_missing` and friends describe a MALFORMED PAYLOAD, which is a client bug, not
 * something an admin did. Writing SMB-friendly copy for those in nine locales would be inventing
 * reassurance for a state that means "this page sent nonsense"; the backend's English message is
 * the right thing to show, because the person who needs to read it is us.
 *
 * So: the reachable set is translated and asserted (`flowIssueMessages.test.ts` pins the list), and
 * anything else falls back to the backend's `message`. The fallback is deliberate and covered, not
 * an omission — which is the distinction a future reader needs, since a missing translation and a
 * declined one look identical in a locale file.
 */
/** One entry from the backend's `detail.issues`. Context keys vary by code. */
export interface FlowIssue {
  code: string;
  message?: string;
  node_id?: string;
  node_index?: number;
  field?: string;
  count?: number;
  [key: string]: unknown;
}

/**
 * Issue codes an admin can reach through the wizard, and therefore must read in their language.
 *
 * Pinned as a SET rather than left implicit so the test can assert every member has a string in
 * all nine locales, and so adding a validation on the backend forces a decision here rather than
 * silently degrading to English.
 */
export const TRANSLATED_FLOW_ISSUE_CODES = [
  // Condition / branch configuration — the owner's case is the first of these.
  'condition_field_missing',
  'condition_field_optional',
  'condition_field_type_invalid',
  'condition_config_invalid',
  'condition_target_invalid',
  'branch_discriminator_missing',
  // Approval configuration.
  'approval_config_missing',
  'approval_sla_invalid',
  'cc_recipients_invalid',
  // Outcome wiring, per node type.
  'approval_outcome_missing',
  'condition_outcome_missing',
  'branch_outcome_missing',
  'unsupported_outcome',
  'duplicate_outcome',
  // Graph shape the canvas can genuinely produce.
  'outgoing_edge_missing',
  'outgoing_edge_count_invalid',
  'end_missing',
  'end_has_outgoing',
  'start_count_invalid',
  'node_unreachable',
  'cycle_detected',
] as const;

const TRANSLATED = new Set<string>(TRANSLATED_FLOW_ISSUE_CODES);

/** Whether this issue has SMB-friendly copy, or falls back to the backend's English message. */
export function isTranslatedFlowIssue(code: string): boolean {
  return TRANSLATED.has(code);
}

/**
 * The line to show for one issue.
 *
 * Context is passed to `t` so a string can name the offending field or step — `condition_field_
 * missing` reads "…branches on a form field named 'amount', but this form doesn't have it", which
 * is the whole difference between an actionable refusal and the one that was reported.
 */
export function flowIssueMessage(
  issue: FlowIssue,
  // LOOSE signature, matching `translateApiError`'s for the same reason recorded there:
  // react-i18next's `TFunction` generics reject an arbitrary string key at compile time, and the
  // key here is built from a backend code. Same shape in both helpers so neither drifts.
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const code = typeof issue.code === 'string' ? issue.code : '';
  if (TRANSLATED.has(code)) {
    return t(`processes:flowIssues.${code}`, {
      field: issue.field ?? '',
      count: typeof issue.count === 'number' ? issue.count : 0,
    });
  }
  // Deliberate fallback (see the file header): a malformed-payload issue is a client bug, and the
  // backend's English sentence is aimed at whoever debugs it.
  const backend = typeof issue.message === 'string' ? issue.message.trim() : '';
  return backend || t('processes:flowIssues.unknown', { code: code || '?' });
}

/**
 * Pull the issue list out of an `invalid_process_flow` refusal.
 *
 * TWO-LEVEL PEEL, because FastAPI wraps a structured detail as `{ detail: {...} }` and axios wraps
 * that again — the same peel `translateApiError` does, and the reason the publish handler already
 * had a hand-rolled copy of it. One implementation, exported, rather than a third.
 */
export function flowIssuesFrom(detail: unknown): FlowIssue[] {
  const outer = detail as { detail?: unknown } | null;
  const inner = outer && typeof outer === 'object' && 'detail' in outer ? outer.detail : outer;
  if (!inner || typeof inner !== 'object') return [];
  const issues = (inner as { issues?: unknown }).issues;
  if (!Array.isArray(issues)) return [];
  return issues.filter(
    (entry): entry is FlowIssue =>
      typeof entry === 'object' && entry !== null && typeof (entry as FlowIssue).code === 'string',
  );
}
